import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../core/services/auth/auth.service';
import { ThemeService } from '../../core/services/theme/theme.service';

/**
 * The header user menu: an avatar button that opens a dropdown with the signed-in
 * user, a theme toggle, an Admin link (admins only) and Log out. Shared by every
 * header (global shell + Execute top bar) so the menu is identical everywhere.
 */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  imports: [CdkMenuTrigger, CdkMenu, CdkMenuItem, RouterLink],
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent {
  private auth = inject(AuthService);
  private theme = inject(ThemeService);

  readonly isAdmin = this.auth.isAdminSig;
  readonly isDark = this.theme.isDark;

  readonly initials = computed(() => {
    const u = this.auth.userSig();
    if (!u) return '';
    const i1 = (u.firstName || '').trim()[0] ?? '';
    const i2 = (u.lastName || '').trim()[0] ?? '';
    return (i1 + i2).toUpperCase();
  });

  readonly fullName = computed(() => {
    const u = this.auth.userSig();
    if (!u) return '';
    return `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
  });

  readonly email = computed(() => this.auth.userSig()?.email ?? '');

  toggleTheme(): void {
    this.theme.toggle();
  }

  logout(): void {
    this.auth.logout();
  }
}
